import React, { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Format a JS Date as "YYYY-MM-DDTHH:mm" (the same shape datetime-local used).
// Not using toISOString() because that's UTC and would shift by the user offset.
export function toLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function parseLocalValue(value: string): { date: Date | undefined; time: string } {
  if (!value) return { date: undefined, time: "00:00" };
  const [datePart, timePart = "00:00"] = value.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return { date: undefined, time: timePart.slice(0, 5) };
  // Construct local Date (months are 0-based)
  const date = new Date(y, m - 1, d);
  return { date, time: timePart.slice(0, 5) };
}

export function formatDisplay(value: string): string {
  const { date, time } = parseLocalValue(value);
  if (!date) return "—";
  return `${date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })}  ${time}`;
}

export interface DateTimeFieldProps {
  value: string;
  onChange: (v: string) => void;
  testId: string;
  label: string;
}

/**
 * shadcn-styled date-time picker: Popover(Calendar) for the date and a
 * native time input (HH:mm) below. State is kept as "YYYY-MM-DDTHH:mm" so
 * the rest of the dialog (rangeIso) stays unchanged. An sr-only
 * datetime-local input mirrors the value so e2e tests can fill it directly.
 */
export const DateTimeField: React.FC<DateTimeFieldProps> = ({ value, onChange, testId, label }) => {
  const [open, setOpen] = useState(false);
  const { date, time } = parseLocalValue(value);

  const applyDate = (newDate: Date | undefined) => {
    if (!newDate) return;
    const pad = (n: number) => String(n).padStart(2, "0");
    const base = `${newDate.getFullYear()}-${pad(newDate.getMonth() + 1)}-${pad(newDate.getDate())}`;
    onChange(`${base}T${time || "00:00"}`);
    setOpen(false);
  };

  const applyTime = (newTime: string) => {
    const base = date ? toLocalValue(date).split("T")[0] : toLocalValue(new Date()).split("T")[0];
    onChange(`${base}T${newTime || "00:00"}`);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm text-[#ccc]">{label}</span>
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button
                variant="outline"
                className="flex-1 justify-start font-normal bg-[var(--color-panel-bg)] border-[var(--color-panel-border)] hover:bg-[var(--color-bg-base)] hover:text-white text-white"
                data-testid={`${testId}-date`}
              >
                <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />
                {date ? formatDisplay(value).split("  ")[0] : "Seleccionar fecha"}
              </Button>
            }
          />
          <PopoverContent
            className="w-auto p-0 bg-[var(--color-panel-bg)] border-[var(--color-panel-border)]"
            align="start"
          >
            <Calendar
              mode="single"
              selected={date}
              onSelect={applyDate}
              captionLayout="dropdown"
              autoFocus
            />
          </PopoverContent>
        </Popover>
        <Input
          type="time"
          value={time}
          onChange={(e) => applyTime(e.target.value)}
          className="w-[120px] bg-[var(--color-panel-bg)] border-[var(--color-panel-border)] text-white"
          data-testid={`${testId}-time`}
        />
      </div>
      {/* e2e escape hatch: fillable with a full "YYYY-MM-DDTHH:mm" value. */}
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
      />
    </div>
  );
};
